import { parseMoneyTextToCents, centsToMajorString } from "../utils/money";

const SELECTOR_PRODUCT_ROOT = "[data-product-info]";
const SELECTOR_PRICE_CONTAINER =
  ".product-info__block-item[data-block-type='price'] .product-info__price";
const SELECTOR_AFTERPAY_PLACEMENT = "square-placement";

const getCurrentPriceCents = (productRoot: HTMLElement): number | null => {
  const priceContainer = productRoot.querySelector<HTMLElement>(
    SELECTOR_PRICE_CONTAINER
  );
  if (!priceContainer) return null;

  const saleEl = priceContainer.querySelector<HTMLElement>(
    "[data-price-sale], .text-on-sale"
  );
  if (saleEl) {
    const cents = parseMoneyTextToCents(saleEl.textContent || "");
    if (cents !== null) return cents;
    return null;
  }

  const regularAttrEl = priceContainer.querySelector<HTMLElement>(
    "[data-price-regular]"
  );
  if (regularAttrEl) {
    const centsFromText = parseMoneyTextToCents(
      regularAttrEl.textContent || "",
    );
    if (centsFromText !== null) return centsFromText;

    const dataRegular = regularAttrEl.dataset.priceRegular;
    if (dataRegular) {
      const cents = parseInt(dataRegular, 10);
      if (Number.isFinite(cents)) return Math.max(0, cents);
    }
  }

  return null;
};

const updateAfterpayForProduct = (productRoot: HTMLElement): void => {
  const placements = productRoot.querySelectorAll<HTMLElement>(
    SELECTOR_AFTERPAY_PLACEMENT,
  );
  if (placements.length === 0) return;

  const cents = getCurrentPriceCents(productRoot);
  if (cents === null) return;
  const amount = centsToMajorString(cents);

  placements.forEach((placement) => {
    if (placement.getAttribute("data-amount") === amount) return;
    placement.setAttribute("data-amount", amount);
    placement.dispatchEvent(new Event("change"));
  });
};

const boundRoots = new WeakSet<HTMLElement>();

const observeProductPrice = (productRoot: HTMLElement): void => {
  if (boundRoots.has(productRoot)) return;
  const priceContainer = productRoot.querySelector<HTMLElement>(
    SELECTOR_PRICE_CONTAINER
  );
  if (!priceContainer) return;

  updateAfterpayForProduct(productRoot);

  let animationFrameId = 0;
  const observer = new MutationObserver(() => {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    animationFrameId = requestAnimationFrame(() => {
      animationFrameId = 0;
      updateAfterpayForProduct(productRoot);
    });
  });
  observer.observe(priceContainer, {
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  });
  boundRoots.add(productRoot);
};

const bindAfterpay = (): void => {
  document
    .querySelectorAll<HTMLElement>(SELECTOR_PRODUCT_ROOT)
    .forEach(observeProductPrice);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindAfterpay);
} else {
  bindAfterpay();
}

document.addEventListener("shopify:section:load", bindAfterpay);
